import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface OnboardingContextType {
  showOnboarding: boolean;
  setShowOnboarding: (show: boolean) => void;
  completeOnboarding: () => Promise<void>;
  isFirstLogin: boolean;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);

  useEffect(() => {
    const checkFirstLogin = async () => {
      if (!isAuthenticated || !user?.id) {
        return;
      }

      try {
        // Check if user has completed onboarding
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('first_login_at, onboarding_completed')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error('❌ Error checking onboarding status:', error);
          // Still show onboarding if we can't check
          setShowOnboarding(true);
          return;
        }

        // If first_login_at is null, this is their first login
        if (!profile?.first_login_at) {
          setIsFirstLogin(true);
          
          // Update first_login_at
          const { error: updateError } = await supabase
            .from('profiles')
            .update({ first_login_at: new Date().toISOString() })
            .eq('id', user.id);

          if (updateError) {
            console.error('❌ Error updating first_login_at:', updateError);
          } else {
          }

          // Send welcome email on first login
          
          try {
            const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-welcome-email', {
              body: {
                email: user.email,
                name: user.name,
                company_name: user.company_name || null,
                role: user.roles?.[0] || 'user',
              }
            });
            
            if (emailError) {
              console.error('❌ Welcome email failed:', emailError);
            } else {
            }
          } catch (emailErr) {
            console.error('❌ Welcome email error (non-blocking):', emailErr);
          }
        }

        // Show onboarding if not completed yet
        if (!profile?.onboarding_completed) {
          setShowOnboarding(true);
        } else {
        }
      } catch (error) {
        console.error('❌ Error in onboarding check:', error);
        // Default to showing onboarding on error
        setShowOnboarding(true);
      }
    };

    checkFirstLogin();
  }, [isAuthenticated, user?.id, user?.email]);

  const completeOnboarding = async () => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ onboarding_completed: true })
        .eq('id', user.id);

      if (error) {
        console.error('Error completing onboarding:', error);
      } else {
      }
    } catch (error) {
      console.error('Error in completeOnboarding:', error);
    }

    setShowOnboarding(false);
  };

  return (
    <OnboardingContext.Provider value={{ showOnboarding, setShowOnboarding, completeOnboarding, isFirstLogin }}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}
